<script>
	import { onDestroy, tick } from "svelte";
	import { uiText } from "../lib/uiText.js";
	import {
		collection,
		query,
		orderBy,
		onSnapshot,
		addDoc,
		serverTimestamp,
		doc,
		limit,
		updateDoc,
		increment,
	} from "firebase/firestore";
	import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

	export let db;
	export let storage; // Make sure to pass your Firebase Storage instance as a prop
	export let user;
	export let lang = "bg";
	export let sessionData;

	let ui = uiText(lang);
	$: lang, (ui = uiText(lang));

	let messagesArray = [];
	let newMessage = "";
	let fileInput;
	let isUploading = false;
	let chatContainer; // Reference to the scrollable div
	let textInput; // Add this next to your let chatContainer;

	let expandedImage = null;

	let messageLimit = 5; // Start by loading only 5 messages
	let unsubscribe = null; // Hold the listener so we can destroy it
	let isLoadingMore = false; // Prevent rapid-fire scrolling triggers

	const messagesRef = collection(
		db,
		"sessions",
		sessionData.session.sid,
		"messages",
	);
	// const q = query(messagesRef, orderBy("createdAt", "asc"));

	// const unsubscribe = onSnapshot(q, async (snapshot) => {
	// 	messagesArray = snapshot.docs.map((doc) => ({
	// 		id: doc.id,
	// 		...doc.data(),
	// 	}));

	// 	// Auto-scroll to bottom when new messages arrive
	// 	await tick();
	// 	if (chatContainer) {
	// 		// Replaced standard scrollTop with smooth behavior
	// 		chatContainer.scrollTo({
	// 			top: chatContainer.scrollHeight,
	// 			behavior: "smooth",
	// 		});
	// 	}
	// });

	// Create a function to handle the subscription dynamically
	function subscribeToMessages(currentLimit) {
		// If there's an existing listener, kill it before making a new one
		if (unsubscribe) unsubscribe();

		// 1. Query DESCENDING to get the newest messages, and apply the limit
		const q = query(
			messagesRef,
			orderBy("createdAt", "desc"),
			limit(currentLimit),
		);

		unsubscribe = onSnapshot(q, async (snapshot) => {
			// Save the container's height before we add new DOM elements
			let oldScrollHeight = chatContainer ? chatContainer.scrollHeight : 0;

			// Check if the user is currently looking at the very bottom
			let isAtBottom = chatContainer
				? chatContainer.scrollHeight - chatContainer.scrollTop <=
					chatContainer.clientHeight + 50
				: true;

			// 2. Map the data, then REVERSE it so it reads top-to-bottom
			messagesArray = snapshot.docs
				.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}))
				.reverse();

			isLoadingMore = false; // Reset the loading lock

			await tick();

			if (chatContainer) {
				if (currentLimit === 5 || isAtBottom) {
					// If it's the initial load OR a brand new message just arrived at the bottom
					chatContainer.scrollTo({
						top: chatContainer.scrollHeight,
						behavior: "smooth",
					});
				} else if (currentLimit > 5 && !isAtBottom) {
					// If they scrolled up to load older messages, adjust the scroll position
					// so the text doesn't suddenly jump out of their view
					let newScrollHeight = chatContainer.scrollHeight;
					chatContainer.scrollTop =
						chatContainer.scrollTop + (newScrollHeight - oldScrollHeight);
				}
			}
		});
	}

	// Initialize the first subscription
	subscribeToMessages(messageLimit);

	// Handle scroll to top
	function handleScroll() {
		if (!chatContainer) return;

		// If they hit the absolute top and we aren't already loading
		if (chatContainer.scrollTop === 0 && !isLoadingMore) {
			// Check if we actually have more messages to load
			// (if the array length equals our limit, there might be more)
			if (messagesArray.length === messageLimit) {
				isLoadingMore = true;
				messageLimit += 8; // Load 10 older messages
				subscribeToMessages(messageLimit);
			}
		}
	}

	onDestroy(() => {
		if (unsubscribe) unsubscribe();
	});

	// Determine the role for the current user
	$: currentRole = user?.chatRole === "admin" ? "admin" : "player";

	async function sendTextMessage() {
		if (!newMessage.trim()) return;

		await addDoc(messagesRef, {
			text: newMessage,
			senderId: user.uid,
			senderName: user.name || "Player",
			role: currentRole,
			type: "text",
			createdAt: serverTimestamp(),
		});

		const sessionRef = doc(db, "sessions", sessionData.session.sid);
		await updateDoc(sessionRef, {
			unreadPlayerMessages: increment(1),
		});

		newMessage = "";

		// Bring focus back to the input so they can keep typing
		if (textInput) textInput.focus();
	}

	async function handleImageUpload(event) {
		const file = event.target.files[0];
		if (!file) return;

		isUploading = true;
		try {
			// 1. Create a unique path in Storage
			const filename = `${Date.now()}_${file.name}`;
			const storageRef = ref(
				storage,
				`sessions/${sessionData.session.sid}/chat/${filename}`,
			);

			// 2. Upload the file
			const snapshot = await uploadBytes(storageRef, file);

			// 3. Get the public download URL
			const downloadURL = await getDownloadURL(snapshot.ref);

			// 4. Save the URL as a message document
			await addDoc(messagesRef, {
				text: "Image uploaded", // Fallback text
				imageUrl: downloadURL,
				senderId: user.uid,
				senderName: user.name || "Player",
				role: currentRole,
				type: "image",
				createdAt: serverTimestamp(),
			});
		} catch (error) {
			console.error("Image upload failed:", error);
			alert("Failed to upload image.");
		} finally {
			isUploading = false;
			// Reset the input so the user can upload the same file again if needed
			fileInput.value = "";
		}
	}
</script>

<div class="chat-wrapper obelisk">
	<div class="messages" bind:this={chatContainer} on:scroll={handleScroll}>
		{#if isLoadingMore}
			<div class="text-center opacity-50 text-xs my-2">
				<i class="fa-solid fa-circle-notch fa-spin" />
			</div>
		{/if}

		{#each messagesArray as msg (msg.id)}
			<div
				class="msg-bubble"
				class:admin={msg.role === "admin"}
				class:me={msg.senderId === user.uid && msg.role !== "admin"}
			>
				<div class="msg-header" class:admin={msg.role === "admin"}>
					<!-- {#if msg.role === "admin"}
						<span class="badge">{ui.game.footer.vendor}</span>
					{:else}
						<strong>{msg.senderName}</strong>
					{/if} -->
					{#if msg.senderName !== "" && msg.senderName !== "false" && msg.senderName !== false}
						<strong class="mr-1.5">{msg.senderName}</strong>
					{/if}
					{#if msg.role === "admin"}
						<span class="badge"
							>{msg.vendor ? msg.vendor : ui.game.footer.vendor}</span
						>
					{/if}
				</div>

				{#if msg.type === "image"}
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
					<img
						src={msg.imageUrl}
						alt="{ui.chat.uploaded_by} {msg.senderName}"
						class="chat-img"
						style="cursor: zoom-in;"
						on:click={() => (expandedImage = msg.imageUrl)}
					/>
				{:else}
					<div class="msg-text">{msg.text}</div>
				{/if}
				<small class="opacity-60">
					<em>
						{#if msg.createdAt}
							{msg.createdAt
								.toDate()
								.toLocaleTimeString(`${lang}-${lang.toUpperCase()}`, {
									hour: "2-digit",
									minute: "2-digit",
								})}
						{:else}
							<!-- Fallback while uploading to Firebase -->
							{ui.chat?.sending || "..."}
						{/if}
					</em>
				</small>
			</div>
		{/each}
	</div>

	<!-- Conditionally render the input area -->
	{#if sessionData.playerInputEnabled === true || currentRole === "admin"}
		<div class="chat-input-area">
			<input
				id="image-input-hidden"
				type="file"
				accept="image/*"
				bind:this={fileInput}
				on:change={handleImageUpload}
				style="display: none;"
			/>

			<button
				class="upload-btn"
				on:click={() => fileInput.click()}
				disabled={isUploading}
				title={ui.chat.send_image}
			>
				<i
					class=" fa-solid {isUploading
						? 'fa-circle-notch fa-spin'
						: 'fa-image'}"
				></i>
			</button>

			<input
				id="message-input"
				type="text"
				bind:this={textInput}
				bind:value={newMessage}
				on:keydown={(e) => e.key === "Enter" && sendTextMessage()}
				placeholder={ui.chat.type_message}
				size="1"
			/>
			<!-- {ui.chat.send} -->
			<button on:click={sendTextMessage}>
				<i class="fa-solid fa-paper-plane-top"></i></button
			>
		</div>
	{:else}
		<!-- The Disabled State -->
		<div class="chat-input-area disabled-msg text-center opacity-60 italic">
			<p class="mx-auto">
				{ui.chat?.input_disabled || "Player chat is currently disabled."}
			</p>
		</div>
	{/if}
</div>

<!-- FULLSCREEN IMAGE MODAL -->
{#if expandedImage}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div class="image-modal" on:click={() => (expandedImage = null)}>
		<!-- Explicit Close Button -->
		<button
			class="modal-close-btn"
			on:click|stopPropagation={() => (expandedImage = null)}
		>
			<i class="fa-solid fa-times"></i>
		</button>

		<!-- stopPropagation prevents zooming/tapping the image from closing the modal -->
		<img src={expandedImage} alt="Expanded view" on:click|stopPropagation />
	</div>
{/if}

<style>
	.chat-wrapper {
		display: flex;
		flex-direction: column;
		height: calc(100dvh - 80px);
		background: #0004;
		/* border: 1px solid #ccc; */
		/* border-radius: 8px; */
	}

	.messages {
		flex: 1;
		overflow-y: auto;
		padding: 1rem;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.msg-bubble {
		padding: 6px 10px;
		border-radius: 8px;
		background: #c2dcff;
		color: #00214d;
		max-width: 80%;
		align-self: flex-start; /* Aligns player messages to the left */
	}

	/* Current user's messages align right */
	.msg-bubble.me {
		/* background: #fdda00; */
		background: white;
		color: #00214d;
		align-self: flex-end;
	}
	.msg-bubble.me small {
		text-align: right;
	}

	/* GM messages stand out and span the middle or opposite side */
	.msg-bubble.admin {
		background: #fdda00;
		/* background: #00214d; */
		color: #00214d;

		/* border-bottom: 4px solid #00e8bd; */
		align-self: flex-start;
		width: 100%; /* Make GM messages highly visible */
	}

	.msg-header:not(.admin) {
		font-size: 1rem;
		margin-bottom: 4px;
		font-style: italic;
		opacity: 0.6;
	}

	.msg-header.admin {
		font-size: 1rem;
		margin-bottom: 4px;
	}

	.badge {
		background: #00214d;
		color: white;
		padding: 2px 6px;
		border-radius: 4px;
		font-size: 1rem;
		/* margin-left: 6px; */
	}

	.chat-img {
		max-width: 100%;
		/* border-radius: 4px; */
		margin-top: 4px;
	}

	.chat-input-area {
		display: flex;
		/* gap: 8px; */
		/* padding: 10px; */
		border-top: 1px solid #003a8b;
		background: #00214d;
	}

	.chat-input-area input[type="text"] {
		flex: 1;
		padding: 0 8px;
		max-width: 90%;
		/* border-radius: 4px; */
		/* border: 1px solid #ccc; */
	}

	.upload-btn {
		/* background: none; */
		/* border: 1px solid #fff; */
		font-size: 1.2rem;
		cursor: pointer;
		/* padding: 10px 10px; */
		/* aspect-ratio: 1/1; */
		/* border-radius: 4px; */
	}

	.image-modal {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100dvh;
		background: rgba(0, 0, 0, 0.95); /* Darkened slightly for better contrast */
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 99999;
		/* Removed the 20px padding so it can go edge-to-edge */
	}

	.image-modal img {
		width: 100%;
		height: 100%;
		object-fit: contain;
		/* Re-enable native touch gestures so players can pinch-to-zoom */
		touch-action: auto;
	}

	.modal-close-btn {
		position: absolute;
		top: 20px;
		right: 20px;
		background: rgba(0, 0, 0, 0.5);
		color: white;
		border: 2px solid white;
		border-radius: 50%;
		width: 44px; /* Big enough for a thumb tap */
		height: 44px;
		font-size: 1.2rem;
		cursor: pointer;
		z-index: 100000; /* Must sit above the image */
		display: flex;
		justify-content: center;
		align-items: center;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
	}
</style>
